/**
 * CONNECTIONS — the customer's central integrations page.
 *
 *   GET/POST   /orgs/{orgId}/connectors
 *   PATCH/DEL  /orgs/{orgId}/connectors/{id}
 *   POST       /orgs/{orgId}/connectors/{id}/test
 *   GET        /orgs/{orgId}/inbound-emails   (counts only — the messages live on /app/inbox)
 *
 * ONE page, one row per connector — name, type, status, last tested, last activity, and the
 * three actions (Configure · Test · Enable/Disable). "Add connector" opens the SAME reusable
 * form every type is configured through (components/ConnectorFields.tsx), built from the field
 * definitions the API sends. There is deliberately no per-connector screen: the four hard-coded
 * cards this replaces were already three too many.
 *
 * WHAT THE PAGE IS CAREFUL ABOUT:
 *   - It distinguishes what TalentHop runs from what needs the customer's own subscription.
 *   - It never shows a green "connected" for a type whose transport isn't implemented.
 *   - It never displays a stored credential; only which fields hold one.
 *   - It summarises email intake and hands off to the Inbox rather than re-implementing it.
 */
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONNECTOR_CATEGORY_LABELS,
  CONNECTOR_STATUS_LABELS,
  INBOUND_EMAIL_DOMAIN,
  type ConnectorCategory,
  type ConnectorStatus,
} from "@talenthop/shared";
import { api, apiFetch, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Modal } from "../admin/adminUi.js";
import {
  ConnectorFields,
  ProvisioningNote,
  initialValues,
  missingRequired,
  type ConnectorFieldDef,
  type ConnectorFormValues,
} from "../../components/ConnectorFields.js";
import { Link2, Plus, Info, Mail, Alert, RefreshCw, Trash, X } from "../../components/icons.js";

// ── wire types ───────────────────────────────────────────────────────────────

interface Connector {
  id: string;
  typeKey: string;
  typeLabel: string;
  wired: boolean;
  provisioning: "native" | "external_subscription";
  category: ConnectorCategory;
  name: string;
  status: ConnectorStatus;
  enabled: boolean;
  config: ConnectorFormValues;
  secretsSet: string[];
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestNote: string | null;
  lastActivityAt: string | null;
  /** False when the platform has withdrawn the type, or excepted this org from it. */
  available: boolean;
}

interface ConnectorTypeOption {
  key: string;
  label: string;
  description: string;
  category: ConnectorCategory;
  provisioning: "native" | "external_subscription";
  wired: boolean;
  singleton: boolean;
  prerequisite: string | null;
  fields: ConnectorFieldDef[];
  configured: boolean;
}

interface ConnectorsResponse {
  connectors: Connector[];
  types: ConnectorTypeOption[];
}

/** Only the headline counts are read here; the rows themselves belong to the Inbox page. */
interface InboundCounts {
  total: number;
  filed: number;
  needs_review: number;
  unassigned: number;
  dismissed: number;
}

// ── presentation ─────────────────────────────────────────────────────────────

/** The status pill on the Theme 1 semantic palette. Only `active` is green — nothing else has
 *  earned it; `error` is the one blocking state (red); `connected` (tested, not switched on yet)
 *  is in progress (blue); configured / inactive / not configured stay neutral. */
const statusPill = (s: ConnectorStatus): string =>
  s === "active" ? "pill v" : s === "error" ? "pill f" : s === "connected" ? "pill pv" : "pill n";

const when = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
};

const CATEGORY_ORDER: ConnectorCategory[] = ["intake", "outbound", "ats", "job_board"];

// ── page ─────────────────────────────────────────────────────────────────────

export function Connections(): ReactNode {
  const { me } = useAuth();
  const orgId = me?.orgId ?? "";
  const isSuperAdmin = me?.role === "super_admin";
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<Connector | null>(null);
  const [adding, setAdding] = useState<ConnectorTypeOption | null>(null);
  const [picking, setPicking] = useState(false);
  const [values, setValues] = useState<ConnectorFormValues>({});
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; note: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Connector | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["connectors", orgId],
    queryFn: () => api.get<ConnectorsResponse>(`/orgs/${orgId}/connectors`),
    enabled: orgId.length > 0,
  });
  const connectors = useMemo(() => data?.connectors ?? [], [data]);
  const types = useMemo(() => data?.types ?? [], [data]);

  const { data: reqsData } = useQuery({
    queryKey: ["requisitions", orgId],
    queryFn: () => api.get<{ requisitions: Array<{ id: string; title: string; jobCode?: string | null; job_code?: string | null; state?: string }> }>("/requisitions"),
    enabled: orgId.length > 0,
  });
  const requisitions = useMemo(() => reqsData?.requisitions ?? [], [reqsData]);

  // The intake log doubles as the email connector's "last activity" evidence. limit=1 because
  // this page wants the counts, not the messages — the Inbox page reads the rows.
  const { data: inbound } = useQuery({
    queryKey: ["inbound-emails", orgId],
    queryFn: () => api.get<{ counts: InboundCounts }>(`/orgs/${orgId}/inbound-emails?limit=1`),
    enabled: orgId.length > 0 && connectors.some((c) => c.typeKey === "email_receive"),
  });
  const counts = inbound?.counts ?? { total: 0, filed: 0, needs_review: 0, unassigned: 0, dismissed: 0 };

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["connectors", orgId] });
    void queryClient.invalidateQueries({ queryKey: ["inbound-emails", orgId] });
  };

  const closeForm = (): void => {
    setEditing(null);
    setAdding(null);
    setFormError(null);
    setValues({});
    setName("");
  };

  const save = useMutation({
    mutationFn: async () => {
      if (adding) {
        return api.post(`/orgs/${orgId}/connectors`, {
          typeKey: adding.key,
          name: name.trim() || adding.label,
          config: values,
          enabled: false, // a new connector is never born switched on — test it first
        });
      }
      return apiFetch(`/orgs/${orgId}/connectors/${editing!.id}`, {
        method: "PATCH",
        body: { name: name.trim() || editing!.name, config: values },
      });
    },
    onSuccess: () => {
      closeForm();
      refresh();
    },
    onError: (err) => {
      setFormError(
        err instanceof ApiError && err.status === 400
          ? "Some fields need attention — check the required ones."
          : err instanceof ApiError && err.status === 403
            ? "This connector isn't available to your organisation."
            : err instanceof ApiError && err.status === 409
              ? "That connector is already configured — edit the existing one."
              : "Couldn't save. Please retry in a moment.",
      );
    },
  });

  const toggle = useMutation({
    mutationFn: (c: Connector) =>
      apiFetch(`/orgs/${orgId}/connectors/${c.id}`, { method: "PATCH", body: { enabled: !c.enabled } }),
    onSuccess: refresh,
  });

  const test = useMutation({
    mutationFn: (c: Connector) =>
      api.post<{ result: { ok: boolean; note: string } }>(`/orgs/${orgId}/connectors/${c.id}/test`, {}),
    onSuccess: (res, c) => {
      setTestResult({ id: c.id, ok: res.result.ok, note: res.result.note });
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (c: Connector) => apiFetch(`/orgs/${orgId}/connectors/${c.id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDelete(null);
      refresh();
    },
  });

  const openEdit = (c: Connector): void => {
    const def = types.find((t) => t.key === c.typeKey);
    setFormError(null);
    setEditing(c);
    setAdding(null);
    setName(c.name);
    setValues(initialValues(def?.fields ?? [], c.config));
  };

  const openAdd = (t: ConnectorTypeOption): void => {
    setFormError(null);
    setPicking(false);
    setAdding(t);
    setEditing(null);
    setName(t.label);
    setValues(initialValues(t.fields));
  };

  const activeType = adding ?? types.find((t) => t.key === editing?.typeKey) ?? null;
  const fields = activeType?.fields ?? [];
  const missing = missingRequired(fields, values, editing?.secretsSet ?? []);

  const emailConnector = connectors.find((c) => c.typeKey === "email_receive") ?? null;
  const addable = types.filter((t) => !(t.singleton && t.configured));

  return (
    <section className="t1s-page t1s-conn">
      <div className="page-h">
        <div>
          <h1>Connections</h1>
          <div className="sub">
            Where candidates come in from and where updates go out to · live from the API
          </div>
        </div>
        {isSuperAdmin && (
          <button className="btn primary" onClick={() => setPicking(true)} disabled={addable.length === 0}>
            <Plus /> Add connector
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="banner t1s-neutral">
          <Info />
          <div>
            Connections are managed by your organisation's super admin. You can see what's
            configured here, but changing it needs their access.
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading your connections…</div>}
      {isError && (
        <div className="banner crit">
          <Alert />
          <div>Couldn't load your connections. Please retry in a moment.</div>
        </div>
      )}

      {/* ── the connector rows, grouped by what they're for ────────────────── */}
      {!isLoading && connectors.length === 0 && (
        <div className="card t1s-empty">
          <div className="t1s-empty-icon"><Link2 /></div>
          <h2>No connections yet</h2>
          <p>
            Add one to bring candidates in automatically — start with Email receiving, which gives
            every job its own address applications can be forwarded to.
          </p>
          {isSuperAdmin && (
            <div className="t1s-actions">
              <button className="btn primary" onClick={() => setPicking(true)}>
                <Plus /> Add connector
              </button>
            </div>
          )}
        </div>
      )}

      {CATEGORY_ORDER.map((cat) => {
        const inCat = connectors.filter((c) => c.category === cat);
        if (inCat.length === 0) return null;
        return (
          <div key={cat}>
            <div className="t1s-sechead">
              <h2>{CONNECTOR_CATEGORY_LABELS[cat]}</h2>
              <span className="mut">{inCat.length} connector{inCat.length === 1 ? "" : "s"}</span>
            </div>
            <div className="t1s-conn-grid">
              {inCat.map((c) => (
                <div key={c.id} className="card t1s-connector">
                  <div className="t1s-connector-h">
                    <span className="t1s-conn-icon" aria-hidden="true">
                      {c.typeKey === "email_receive" ? <Mail /> : <Link2 />}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t1s-connector-name t1s-ellipsis" title={c.name}>{c.name}</div>
                      <div className="t1s-connector-sub">
                        {c.typeLabel} ·{" "}
                        {c.provisioning === "external_subscription" ? "Your own account" : "Run by TalentHop"}
                      </div>
                    </div>
                    <span className={statusPill(c.status)}>{CONNECTOR_STATUS_LABELS[c.status]}</span>
                  </div>

                  <dl className="t1s-meta">
                    <div>
                      <dt>Enabled</dt>
                      <dd>{c.enabled ? "On" : "Off"}</dd>
                    </div>
                    <div>
                      <dt>Last tested</dt>
                      <dd>
                        {when(c.lastTestedAt)}
                        {c.lastTestOk === false && <span className="pill f">failed</span>}
                      </dd>
                    </div>
                    <div>
                      <dt>Last activity</dt>
                      <dd>{when(c.lastActivityAt)}</dd>
                    </div>
                  </dl>

                  {/* The key explanation used to be 12px muted text (audit 19) — now a callout. */}
                  {(!c.wired || !c.available) && (
                    <div className="t1s-callout">
                      <Alert />
                      <div>
                        {!c.available
                          ? "Unavailable to your org — TalentHop has withdrawn this connector or excepted your organisation from it."
                          : "Not live yet — settings are saved, but this connector stays inactive until TalentHop ships it. Nothing is sent or received meanwhile."}
                      </div>
                    </div>
                  )}

                  {/* The test verdict, in full, on the card it belongs to. Shown verbatim —
                      an honest "not implemented yet" is more useful than a red dot. */}
                  {testResult && testResult.id === c.id && (
                    <div className={`banner ${testResult.ok ? "ok" : "warn"}`}>
                      {testResult.ok ? <Info /> : <Alert />}
                      <div style={{ flex: 1 }}>{testResult.note}</div>
                      <button
                        className="btn sm ghost t1s-icon-btn"
                        aria-label="Dismiss test result"
                        data-tip="Dismiss"
                        data-tip-pos="left"
                        onClick={() => setTestResult(null)}
                      >
                        <X />
                      </button>
                    </div>
                  )}

                  {/* Enable is THE next step for an inactive connector, so it's the primary;
                      delete sits apart as a destructive icon (audit 19). */}
                  <div className="t1s-actions">
                    <button
                      className={`btn sm${c.enabled ? "" : " primary"}`}
                      onClick={() => toggle.mutate(c)}
                      disabled={!isSuperAdmin || !c.available || toggle.isPending}
                    >
                      {c.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="btn sm"
                      onClick={() => test.mutate(c)}
                      disabled={!isSuperAdmin || !c.available || test.isPending}
                    >
                      <RefreshCw /> Test
                    </button>
                    <button className="btn sm ghost" onClick={() => openEdit(c)} disabled={!isSuperAdmin}>
                      Configure
                    </button>
                    <button
                      className="btn sm ghost danger t1s-icon-btn t1s-del"
                      onClick={() => setConfirmDelete(c)}
                      disabled={!isSuperAdmin}
                      aria-label={`Remove ${c.name}`}
                      data-tip={`Remove ${c.name}`}
                      data-tip-pos="left"
                    >
                      <Trash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── email intake: the address scheme + what has actually arrived ───── */}
      {emailConnector && (
        <div className="card t1s-email">
          <div className="t1s-connector-h" style={{ marginBottom: 12, alignItems: "center" }}>
            <span className="t1s-conn-icon" aria-hidden="true"><Mail /></span>
            <div className="t1s-connector-name" style={{ flex: 1 }}>Applications by email</div>
            <span className={statusPill(emailConnector.status)}>
              {CONNECTOR_STATUS_LABELS[emailConnector.status]}
            </span>
          </div>
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            Each requirement is assigned a unique email address on <b>{INBOUND_EMAIL_DOMAIN}</b> (e.g. <code>job-12345@{INBOUND_EMAIL_DOMAIN}</code>).
            Forward candidate application emails to the job's address to automatically receive and process resumes.
          </div>

          {/* THE LOG LIVES ON THE INBOX PAGE, not here. Connections is where a super admin
              configures a transport once; the mailbox is a recruiter's daily list, needs
              filtering, searching and dismissing, and had none of that as a table stapled to
              the bottom of this page. This is the summary and the way through to it. */}
          <div className="t1s-email-stats">
            <span className="mut sm">
              {counts.total === 0 ? (
                "Nothing received yet."
              ) : (
                <>
                  <b>{counts.total}</b> received · <b>{counts.filed}</b> added to a job
                  {counts.needs_review > 0 && (
                    <>
                      {" "}
                      · <b>{counts.needs_review}</b> need review
                    </>
                  )}
                  {counts.unassigned > 0 && (
                    <>
                      {" "}
                      · <b>{counts.unassigned}</b> on a deleted job
                    </>
                  )}
                </>
              )}
            </span>
            <Link to="/app/inbox" className="btn sm" style={{ marginLeft: "auto" }}>
              <Mail /> Open Inbox
            </Link>
          </div>
        </div>
      )}

      {/* ── add: pick a type ───────────────────────────────────────────────── */}
      {picking && (
        <Modal title="Add a connector" onClose={() => setPicking(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {addable.length === 0 && (
              <div className="mut sm">Every connector available to your organisation is already configured.</div>
            )}
            {addable.map((t) => (
              <button key={t.key} type="button" className="t1s-pick" onClick={() => openAdd(t)}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 650 }}>{t.label}</span>
                  {/* The native/external distinction is stated on the PICKER, not discovered
                      later at test time when a subscription turns out to be missing. */}
                  <span className={t.provisioning === "native" ? "pill t" : "pill n"}>
                    {t.provisioning === "native" ? "TalentHop" : "Your own account"}
                  </span>
                  {!t.wired && <span className="pill n">not live yet</span>}
                  {t.configured && <span className="pill n">configured</span>}
                </div>
                <div className="mut sm" style={{ marginTop: 4 }}>{t.description}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* ── configure: ONE form, every type ────────────────────────────────── */}
      {activeType && (editing || adding) && (
        <Modal
          title={adding ? `Add ${adding.label}` : `Configure ${editing!.name}`}
          maxWidth={680}
          onClose={closeForm}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
              {missing.length > 0 && (
                <span className="mut sm" style={{ marginRight: "auto" }}>
                  Still needed: {missing.join(", ")}
                </span>
              )}
              <button className="btn ghost" onClick={closeForm}>
                Cancel
              </button>
              <button className="btn primary" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          }
        >
          <ProvisioningNote
            provisioning={activeType.provisioning}
            prerequisite={activeType.prerequisite}
            wired={activeType.wired}
            label={activeType.label}
          />

          {formError && (
            <div className="banner crit" style={{ marginBottom: 14 }}>
              <Alert />
              <div>{formError}</div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label className="t1s-cf-label" htmlFor="conn-name">
              Name
            </label>
            <input
              id="conn-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={activeType.label}
              style={{ width: "100%" }}
            />
            <div className="t1s-cf-help">
              What you'll recognise it by — useful when you have more than one of a kind.
            </div>
          </div>

          <ConnectorFields
            typeKey={activeType.key}
            requisitions={requisitions}
            fields={fields}
            values={values}
            secretsSet={editing?.secretsSet ?? []}
            onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
            disabled={save.isPending}
          />

          <div className="mut sm" style={{ marginTop: 16 }}>
            Saving doesn't switch anything on. Test the connection first, then enable it from the
            list.
          </div>
        </Modal>
      )}

      {/* ── remove ─────────────────────────────────────────────────────────── */}
      {confirmDelete && (
        <Modal
          title={`Remove ${confirmDelete.name}?`}
          onClose={() => setConfirmDelete(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setConfirmDelete(null)}>
                Keep it
              </button>
              <button
                className="btn danger"
                onClick={() => remove.mutate(confirmDelete)}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Removing…" : "Remove"}
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ lineHeight: 1.6 }}>
            Its configuration and any stored credentials are deleted. Candidates already imported
            through it are unaffected and stay on their jobs.
            {confirmDelete.typeKey === "email_receive" && (
              <div style={{ marginTop: 10 }}>
                <b>Applications forwarded to your job addresses will stop being imported.</b> The
                addresses themselves keep existing, so mail sent to them will be received and
                parked for review rather than filed.
              </div>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
